/**
 * E-Mail-Erstellung Tab Komponente
 */

import React, { useCallback, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';
import { useEmailCreation } from '../hooks/useEmailCreation';
import { useI18n } from '../hooks/useI18n';
import { useAppAppearance } from '../theme/AppAppearanceContext';
import {
  honeycombClearIconButtonSx,
  isHoneycombAppearance,
  isHoneycombLightAppearance,
} from '../theme/appAppearance';
import {
  hologramContainedButtonSx,
  hologramFieldSx,
  hologramIconButtonSx,
  hologramOutlinedButtonSx,
} from '../theme/hologramChrome';
import { LANGUAGES } from '../utils/constants';

export function EmailCreationTab() {
  const {
    inputText,
    setInputText,
    resultText,
    targetLang,
    setTargetLang,
    formal,
    setFormal,
    asMail,
    setAsMail,
    isCreating,
    anonymized,
    error,
    createEmail,
    copyToClipboard,
    reset,
  } = useEmailCreation();
  const { t } = useI18n();
  const theme = useTheme();
  const { appearance, hologram } = useAppAppearance();
  const accent = theme.palette.primary.main;
  const fieldSx = hologram ? hologramFieldSx(accent) : {};
  const containedSx = hologram ? hologramContainedButtonSx(accent) : {};
  const outlinedSx = hologram ? hologramOutlinedButtonSx(accent) : {};
  const clearIconSx = hologram
    ? { ...hologramIconButtonSx(accent), color: 'inherit' }
    : isHoneycombAppearance(appearance) && !isHoneycombLightAppearance(appearance)
      ? honeycombClearIconButtonSx()
      : {
          bgcolor: 'error.main',
          color: 'white',
          '&:hover': { bgcolor: 'error.dark' },
          '&:disabled': { bgcolor: 'action.disabledBackground' },
        };
  const resultInputRef = useRef(null);

  const handleCopy = useCallback(async () => {
    const ok = await copyToClipboard();
    if (ok) return;
    const input = resultInputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, [copyToClipboard]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 3, md: 4 } }}>
      <Box sx={{ position: 'relative' }}>
        <TextField
          key={asMail ? 'input-mail' : 'input-plain'}
          label={asMail ? t.creation.inputLabel : t.creation.inputLabelPlain}
          variant="outlined"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          sx={{
            width: '100%',
            '& .MuiInputBase-root': {
              fontSize: { xs: '0.875rem', md: '1rem' },
            },
            ...fieldSx,
          }}
          multiline
          minRows={8}
          maxRows={24}
          fullWidth
        />
        <Tooltip title={t.common.clearAll}>
          <IconButton
            onClick={reset}
            disabled={isCreating || (!inputText && !resultText)}
            sx={{
              position: 'absolute',
              bottom: { xs: 8, md: 8 },
              right: { xs: 8, md: 8 },
              minWidth: 44,
              minHeight: 44,
              width: 44,
              height: 44,
              p: 0,
              zIndex: 1,
              ...clearIconSx,
            }}
          >
            <ClearIcon sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }} />
          </IconButton>
        </Tooltip>
      </Box>

      {anonymized && (
        <Alert
          severity="info"
          sx={{ mt: 2, fontSize: { xs: '0.75rem', md: '0.875rem' } }}
        >
          {t.common.anonymizedNotice.replace('{service}', t.common.textService)}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          flexWrap: 'wrap',
          mb: 2,
          gap: { xs: 2, sm: 1.5 },
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={createEmail}
          disabled={isCreating || !inputText}
          sx={{
            maxWidth: { xs: '100%', sm: 250 },
            minHeight: 44,
            fontWeight: 600,
            letterSpacing: 0.5,
            fontSize: { xs: '0.9375rem', md: '1rem' },
            ...containedSx,
          }}
        >
          {isCreating ? <CircularProgress size={24} /> : t.creation.button}
        </Button>

        <FormControl
          size="small"
          sx={{ minWidth: 140, width: { xs: '100%', sm: 'auto' } }}
        >
          <InputLabel id="tab2-lang-label">{t.common.targetLang}</InputLabel>
          <Select
            labelId="tab2-lang-label"
            value={targetLang}
            label={t.common.targetLang}
            onChange={(e) => setTargetLang(e.target.value)}
            sx={{
              ...(hologram
                ? {
                    borderRadius: 0,
                    color: 'rgba(232, 246, 255, 0.96)',
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: alphaBorder(accent, 0.45),
                    },
                  }
                : {}),
              '& .MuiSelect-select': {
                fontSize: { xs: '0.875rem', md: '1rem' },
                minHeight: { xs: 48, md: 40 },
              },
            }}
          >
            {LANGUAGES.map((lang) => (
              <MenuItem
                key={lang.code}
                value={lang.code}
                sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
              >
                {t.languages[lang.code]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: 'nowrap',
            gap: { xs: 1, sm: 1.5 },
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={asMail}
                onChange={(e) => setAsMail(e.target.checked)}
                sx={
                  hologram
                    ? {
                        color: accent,
                        '&.Mui-checked': { color: accent },
                      }
                    : undefined
                }
              />
            }
            label={t.creation.mailCheckbox}
            sx={{
              mr: 0,
              '& .MuiFormControlLabel-label': {
                fontSize: { xs: '0.875rem', md: '1rem' },
              },
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={formal}
                onChange={(e) => setFormal(e.target.checked)}
                sx={
                  hologram
                    ? {
                        color: accent,
                        '&.Mui-checked': { color: accent },
                      }
                    : undefined
                }
              />
            }
            label={t.creation.formalCheckbox}
            sx={{
              mr: 0,
              '& .MuiFormControlLabel-label': {
                fontSize: { xs: '0.875rem', md: '1rem' },
              },
            }}
          />
        </Box>
      </Box>

      <Box sx={{ position: 'relative' }}>
        <TextField
          key={asMail ? 'output-mail' : 'output-plain'}
          label={
            asMail ? t.creation.outputLabel : t.creation.outputLabelPlain
          }
          variant="outlined"
          value={resultText || ''}
          inputRef={resultInputRef}
          sx={{
            width: '100%',
            mt: 0,
            '& .MuiInputBase-root': {
              fontSize: { xs: '0.875rem', md: '1rem' },
            },
            '& .MuiInputBase-input': {
              overflow: 'auto !important',
              maxHeight: '70vh',
            },
            ...fieldSx,
          }}
          multiline
          minRows={8}
          InputProps={{
            readOnly: true,
            sx: {
              maxHeight: '70vh',
              overflow: 'auto',
            },
          }}
          fullWidth
        />
        <Tooltip title={t.common.copyToClipboard}>
          <span style={{ position: 'absolute', bottom: 8, right: 8, zIndex: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              sx={{
                minWidth: 44,
                minHeight: 44,
                width: 44,
                height: 44,
                p: 0,
                ...outlinedSx,
                ...(hologram ? { borderRadius: '50%' } : {}),
              }}
              onClick={() => {
                void handleCopy();
              }}
              disabled={!resultText}
            >
              <ContentCopyIcon
                sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }}
              />
            </Button>
          </span>
        </Tooltip>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
        >
          {error}
        </Alert>
      )}
    </Box>
  );
}

function alphaBorder(hex, a) {
  const h = String(hex || '').replace('#', '');
  if (h.length !== 6) return `rgba(74, 210, 255, ${a})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}
